import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * 부분 UI 를 묶는 간이 에러 바운더리.
 *
 * <p>런 씬에 붙어있는 frontier HUD / delta panel / ending meta 같은 시나리오 특화
 * 위젯이 예기치 않게 터져도 씬 전체(WeeklyTurnScene · EventScene · RunResultScene)
 * 는 계속 동작하도록 하기 위한 안전장치. fallback 은 null (조용히 숨김) 이 기본이며,
 * 필요하면 지정된 fallback 노드를 대신 렌더.
 *
 * <p>콘솔에는 스택 트레이스를 남겨 스모크 중 진단 가능. 프로덕션에서는 원격 로그 수집
 * 이 붙기 전까지는 console 만 사용.
 */
interface SafeBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
    /** 에러 분류용 태그. 콘솔 로그에 prefix 로 찍혀 어느 경계에서 터졌는지 식별. */
    label?: string;
}

interface SafeBoundaryState {
    hasError: boolean;
}

export class SafeBoundary extends Component<SafeBoundaryProps, SafeBoundaryState> {
    state: SafeBoundaryState = { hasError: false };

    static getDerivedStateFromError(): SafeBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        const label = this.props.label ?? "SafeBoundary";
        // 프로덕션 번들에서도 남길 수 있도록 console.error 로 유지.
        // 원격 로그 파이프라인이 붙으면 여기에 Sentry.captureException 같은 훅 추가 지점.
        console.error(`[${label}] Rendering error caught`, error, info.componentStack);
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback ?? null;
        }
        return this.props.children;
    }
}
